// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import {
  InvoiceLineItemSortProperty,
  invoiceLineItemListingSchema,
} from '@/billing/api/types';
import { Sort } from '@/constants/sort';
import { z } from 'zod';

const invoiceLineItemsResponseSchema = z.object({
  data: invoiceLineItemListingSchema,
  metadata: responseMetadataSchema,
});

export interface InvoiceLineItemFilter {
  pageSize: number;
  pageNumber?: number;
  sort?: Sort<InvoiceLineItemSortProperty>;
  search?: string;
  customerKeys?: string[];
  projectKeys?: string[];
  invoiceDateStart?: string;
  invoiceDateEnd?: string;
}

export function serializeInvoiceLineItemFilter(
  filter?: InvoiceLineItemFilter
): string {
  if (!filter) {
    return '';
  }

  const params = new URLSearchParams();

  params.append('limit', filter.pageSize.toString());
  if (filter.pageNumber !== undefined) {
    params.append('page', filter.pageNumber.toString());
  }
  if (filter.sort) {
    params.append('sortBy', filter.sort.property);
    params.append('sortOrder', filter.sort.direction);
  }
  if (filter.search) {
    params.append('search', filter.search);
  }
  if (filter.customerKeys && filter.customerKeys.length > 0) {
    [...filter.customerKeys].sort().forEach((key) => {
      params.append('customerKey', key);
    });
  }
  if (filter.projectKeys && filter.projectKeys.length > 0) {
    [...filter.projectKeys].sort().forEach((key) => {
      params.append('projectKey', key);
    });
  }
  if (filter.invoiceDateStart) {
    params.append('invoiceDateStart', filter.invoiceDateStart);
  }
  if (filter.invoiceDateEnd) {
    params.append('invoiceDateEnd', filter.invoiceDateEnd);
  }

  return params.toString();
}

export async function getInvoiceLineItems(
  filter: InvoiceLineItemFilter = { pageSize: 20 }
) {
  const serializedFilter = serializeInvoiceLineItemFilter(filter);
  const url = serializedFilter
    ? `/v1/invoice-line-item?${serializedFilter}`
    : '/v1/invoice-line-item';
  const response = await api.get(url);

  const result = await parseResponse(
    invoiceLineItemsResponseSchema,
    response.data,
    {
      schemaName: 'InvoiceLineItems',
      endpoint: url,
    }
  );

  return result.data;
}
